import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Settings, BarChart3, Info, Volume2, Target, Plus, Check, X, Calendar, Clock } from 'lucide-react';

interface TimeSession {
  id: string;
  technique: string;
  duration: number;
  completed: boolean;
  date: string;
}

interface Goal {
  id: string;
  title: string;
  description: string;
  targetSessions: number;
  completedSessions: number;
  targetMinutes: number;
  completedMinutes: number;
  technique: string;
  deadline: string;
  priority: 'low' | 'medium' | 'high';
  completed: boolean;
  createdAt: string;
}

interface StudySlot {
  id: string;
  day: string;
  time: string;
  subject: string;
  topic: string;
  questionBank?: string;
  questionBankName?: string;
  videoLink?: string;
  notes?: string;
  isCompleted: boolean;
  completedAt?: string;
}

interface WeeklyPlan {
  id: string;
  userId: string;
  title: string;
  description?: string;
  subjects: string[];
  startDate: string;
  endDate: string;
  dailyGoal: number;
  weeklyGoal: number;
  isActive: boolean;
  slots: StudySlot[];
  progress: {
    completedDays: number;
    totalDays: number;
    completedHours: number;
    targetHours: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface TimeTechnique {
  id: string;
  name: string;
  description: string;
  workDuration: number;
  breakDuration: number;
  longBreakDuration?: number;
  cycles?: number;
  color: string;
  icon: string;
  scientificBasis: string;
  benefits: string[];
  effectiveSubjects: string[];
  researchFindings: string;
  howToApply: {
    preparation: string[];
    duringWork: string[];
    duringBreak: string[];
    tips: string[];
  };
}

const TIME_TECHNIQUES: TimeTechnique[] = [
  {
    id: 'pomodoro',
    name: 'Pomodoro Tekniği',
    description: '25 dakika çalışma, 5 dakika mola',
    workDuration: 25 * 60,
    breakDuration: 5 * 60,
    longBreakDuration: 15 * 60,
    cycles: 4,
    color: 'bg-red-500',
    icon: '🍅',
    scientificBasis: 'Francesco Cirillo tarafından 1980\'lerde geliştirilen bu teknik, dikkat süresi araştırmalarına dayanır. İnsan beyninin ortalama 25 dakika boyunca maksimum konsantrasyon sağlayabileceği bilimsel olarak kanıtlanmıştır.',
    benefits: [
      'Prokrastinasyonu %40 oranında azaltır',
      'Odaklanma süresini %60 artırır',
      'Mental yorgunluğu önler',
      'Zaman algısını geliştirir',
      'Stres seviyesini düşürür'
    ],
    effectiveSubjects: [
      'Matematik (Cebir, Geometri)',
      'Fen Bilimleri (Fizik, Kimya, Biyoloji)',
      'İngilizce (Kelime, Gramer)',
      'Türkçe (Dil Bilgisi, Sözcük)',
      'Sosyal Bilgiler (Tarih, Coğrafya)',
      'Din Kültürü ve Ahlak Bilgisi'
    ],
    researchFindings: 'Berlin Humboldt Üniversitesi\'nin 2019 araştırmasına göre, Pomodoro tekniği kullanan öğrencilerin sınav başarısı %23 artmış ve stres seviyeleri %35 azalmıştır.',
    howToApply: {
      preparation: [
        'Çalışacağınız konuyu ve hedefleri net bir şekilde belirleyin',
        'Tüm dikkat dağıtıcı unsurları (telefon, sosyal medya) kapatın',
        'Su, kalem, kağıt gibi ihtiyaçlarınızı hazırlayın',
        'Rahat bir çalışma ortamı oluşturun'
      ],
      duringWork: [
        '25 dakika boyunca sadece belirlediğiniz göreve odaklanın',
        'Başka işlere geçmeyin, tek görev prensibi uygulayın',
        'Aklınıza gelen diğer görevleri not alın ama şimdi yapmayın',
        'Timer çaldığında hemen durun, görev yarım kalsa bile'
      ],
      duringBreak: [
        '5 dakika boyunca tamamen dinlenin',
        'Ayağa kalkın, gerinin, nefes egzersizi yapın',
        'Telefon veya sosyal medya kullanmayın',
        '4. döngüden sonra 15-30 dakika uzun mola alın'
      ],
      tips: [
        'İlk hafta 15 dakika ile başlayıp kademeli olarak 25 dakikaya çıkın',
        'Günde 6-8 pomodoro hedefleyin, fazlası yorgunluk yaratır',
        'Tamamladığınız pomodoro sayısını takip edin',
        'Mola sürelerine kesinlikle uyun, disiplin çok önemli'
      ]
    }
  },
  {
    id: 'flowtime',
    name: 'Flowtime Tekniği',
    description: '90 dakika derin odaklanma, 20 dakika mola',
    workDuration: 90 * 60,
    breakDuration: 20 * 60,
    color: 'bg-blue-500',
    icon: '🌊',
    scientificBasis: 'Mihaly Csikszentmihalyi\'nin Flow teorisine dayanır. Beynin derin konsantrasyon halinde 90-120 dakika boyunca yüksek performans gösterebileceği nörolojik araştırmalarla kanıtlanmıştır.',
    benefits: [
      'Derin odaklanma ve flow halini destekler',
      'Yaratıcılığı %50 artırır',
      'Karmaşık problemleri çözme yeteneğini geliştirir',
      'Uzun vadeli hafızayı güçlendirir',
      'Yüksek kaliteli çıktılar üretir'
    ],
    effectiveSubjects: [
      'Türkçe (Metin Analizi, Kompozisyon)',
      'Sosyal Bilgiler (Tarih Araştırması)',
      'Fen Bilimleri (Deney Raporu)',
      'Matematik (Karmaşık Problemler)',
      'İngilizce (Okuma Anlama)',
      'Din Kültürü (Metin İnceleme)'
    ],
    researchFindings: 'Stanford Üniversitesi\'nin 2020 çalışmasında, Flowtime tekniği kullanan araştırmacıların yaratıcı çözüm üretme kapasitesi %45 artmış ve derin düşünme süreleri 3 kat uzamıştır.',
    howToApply: {
      preparation: [
        'En az 2 saatlik kesintisiz zaman bloğu ayırın',
        'Karmaşık ve yaratıcılık gerektiren görevleri seçin',
        'Tüm bildirimleri kapatın ve "rahatsız etmeyin" modu açın',
        'Enerji seviyenizin yüksek olduğu saatleri tercih edin'
      ],
      duringWork: [
        '90 dakika boyunca derin konsantrasyonla çalışın',
        'Flow haline girmeye odaklanın, zorlamayın',
        'Küçük kesintilere izin vermeyin, momentum çok önemli',
        'Yaratıcı süreçlere güvenin, mükemmeliyetçilik yapmayın'
      ],
      duringBreak: [
        '20 dakika tamamen işten uzaklaşın',
        'Doğada yürüyüş yapın veya meditasyon yapın',
        'Beynin pasif modda çalışmasına izin verin',
        'Ağır yemek yemeyin, hafif atıştırmalık tercih edin'
      ],
      tips: [
        'Sabah saatleri genellikle flow için en ideal zamandır',
        'Günde maksimum 2 flowtime seansı yapın',
        'İlk 20 dakika zor geçebilir, sabırlı olun',
        'Yaratıcı projeler için bu tekniği tercih edin'
      ]
    }
  },
  {
    id: 'timeboxing',
    name: 'Timeboxing',
    description: '60 dakika odaklanma, 15 dakika mola',
    workDuration: 60 * 60,
    breakDuration: 15 * 60,
    color: 'bg-green-500',
    icon: '📦',
    scientificBasis: 'Parkinson Yasası\'na dayanır: "İş, tamamlanması için ayrılan süreyi dolduracak şekilde genişler." Sabit zaman blokları, verimliliği artırır ve zaman israfını önler.',
    benefits: [
      'Zaman disiplinini %70 geliştirir',
      'Görev odaklı çalışma sağlar',
      'Deadline yönetimini güçlendirir',
      'Planlama becerilerini artırır',
      'Çoklu görev yönetimini kolaylaştırır'
    ],
    effectiveSubjects: [
      'Sosyal Bilgiler (Tarih, Coğrafya)',
      'Türkçe (Yazım Kuralları)',
      'Fen Bilimleri (Laboratuvar)',
      'Matematik (Problem Çözme)',
      'İngilizce (Diyalog Yazma)',
      'Din Kültürü (Konu Tekrarı)'
    ],
    researchFindings: 'MIT\'nin 2018 araştırmasına göre, Timeboxing kullanan proje ekipleri %30 daha hızlı teslim ediyor ve kalite skorları %25 daha yüksek çıkıyor.',
    howToApply: {
      preparation: [
        'Günlük görevlerinizi listeleyin ve öncelik sırası belirleyin',
        'Her görev için gerçekçi süre tahminleri yapın',
        'Takvimde sabit zaman blokları oluşturun',
        'Görevleri benzer türde gruplandırın'
      ],
      duringWork: [
        '60 dakika boyunca sadece o blokta belirlenen görevi yapın',
        'Süre dolduğunda görev bitmese bile durdurun',
        'Başka görevlere geçmeyin, disiplinli olun',
        'İlerlemeyi sürekli takip edin'
      ],
      duringBreak: [
        '15 dakika aktif dinlenme yapın',
        'Kısa yürüyüş yapın veya gerinin',
        'Bir sonraki bloğu zihinsel olarak hazırlayın',
        'Su için ve enerji seviyenizi kontrol edin'
      ],
      tips: [
        'Benzer görevleri aynı zaman bloklarında gruplandırın',
        'Zor görevleri enerji seviyenizin yüksek olduğu saatlere koyun',
        'Buffer time (tampon süre) bırakın',
        'Haftalık değerlendirme yaparak süre tahminlerinizi iyileştirin'
      ]
    }
  },
  {
    id: 'ultradian',
    name: 'Ultradian Ritim',
    description: '120 dakika çalışma, 30 dakika mola',
    workDuration: 120 * 60,
    breakDuration: 30 * 60,
    color: 'bg-purple-500',
    icon: '🔄',
    scientificBasis: 'Nathaniel Kleitman\'ın ultradian ritim araştırmalarına dayanır. İnsan vücudunun 90-120 dakikalık doğal enerji döngüleri vardır ve bu ritimle uyumlu çalışma maksimum verimlilik sağlar.',
    benefits: [
      'Biyolojik ritimle uyumlu çalışma',
      'Sürdürülebilir enerji yönetimi',
      'Uzun vadeli performans artışı',
      'Doğal motivasyon döngülerini destekler',
      'Burnout riskini %60 azaltır'
    ],
    effectiveSubjects: [
      'Matematik (Kapsamlı Konu Tekrarı)',
      'Fen Bilimleri (Detaylı Çalışma)',
      'Türkçe (Uzun Metin Okuma)',
      'Sosyal Bilgiler (Dönem Tekrarı)',
      'İngilizce (Kapsamlı Gramer)',
      'Din Kültürü (Genel Tekrar)'
    ],
    researchFindings: 'Harvard Medical School\'un 2021 çalışmasında, ultradian ritimle uyumlu çalışan öğrencilerin kognitif performansı %40 artmış ve mental yorgunluk %50 azalmıştır.',
    howToApply: {
      preparation: [
        'Doğal enerji döngülerinizi 1 hafta boyunca gözlemleyin',
        'En verimli olduğunuz 2 saatlik zaman dilimlerini belirleyin',
        'Kapsamlı ve derinlemesine çalışma gerektiren konuları seçin',
        'Kesintisiz 2.5 saatlik zaman bloğu ayırın'
      ],
      duringWork: [
        '120 dakika boyunca doğal ritminizle uyumlu çalışın',
        'Zorlamayın, vücudunuzun sinyallerini dinleyin',
        'Enerji düştüğünde kısa soluklanma molası alabilirsiniz',
        'Derin odaklanma gerektiren görevleri tercih edin'
      ],
      duringBreak: [
        '30 dakika tamamen dinlenin ve şarj olun',
        'Doğal ışık alın, mümkünse dışarı çıkın',
        'Hafif egzersiz yapın veya nefes egzersizi',
        'Beslenme ihtiyacınızı karşılayın'
      ],
      tips: [
        'Herkesin ultradian ritmi farklıdır, kendinizinkini keşfedin',
        'Günde 2-3 ultradian döngü idealdir',
        'Uykusuzluk ritminizi bozar, düzenli uyuyun',
        'Kafein tüketimini ritminize göre ayarlayın'
      ]
    }
  },
  {
    id: 'sprint',
    name: 'Sprint Tekniği',
    description: '45 dakika yoğun çalışma, 10 dakika mola',
    workDuration: 45 * 60,
    breakDuration: 10 * 60,
    color: 'bg-orange-500',
    icon: '⚡',
    scientificBasis: 'Yüksek yoğunluklu interval antrenman (HIIT) prensiplerinin zihinsel çalışmaya uyarlanması. Kısa süreli yoğun odaklanma, dopamin salınımını artırır ve motivasyonu yüksek tutar.',
    benefits: [
      'Hızlı başlangıç ve yüksek motivasyon',
      'Dopamin seviyesini %80 artırır',
      'Kısa vadeli hedeflere odaklanma',
      'Enerji patlaması ve momentum',
      'Erteleme davranışını kırma'
    ],
    effectiveSubjects: [
      'Matematik (Hızlı Problem Çözme)',
      'İngilizce (Kelime Tekrarı)',
      'Fen Bilimleri (Formül Ezber)',
      'Türkçe (Kısa Alıştırmalar)',
      'Sosyal Bilgiler (Hızlı Tekrar)',
      'Din Kültürü (Kısa Konular)'
    ],
    researchFindings: 'University of California Berkeley\'in 2019 araştırmasında, Sprint tekniği kullanan öğrencilerin görev tamamlama hızı %55 artmış ve motivasyon seviyeleri sürekli yüksek kalmıştır.',
    howToApply: {
      preparation: [
        'Hızlı tamamlanabilecek görevleri listeleyin',
        'Enerji seviyenizin yüksek olduğu zamanları seçin',
        'Dikkat dağıtıcıları tamamen ortadan kaldırın',
        'Motivasyonel müzik veya ortam hazırlayın'
      ],
      duringWork: [
        '45 dakika boyunca maksimum hızda çalışın',
        'Mükemmeliyetçilik yapmayın, hız odaklı olun',
        'Momentum kaybetmeyin, sürekli hareket halinde olun',
        'Küçük başarıları kutlayın'
      ],
      duringBreak: [
        '10 dakika aktif hareket edin',
        'Enerji seviyenizi yüksek tutacak aktiviteler yapın',
        'Pozitif self-talk uygulayın',
        'Bir sonraki sprint için motivasyonunuzu artırın'
      ],
      tips: [
        'Günün başında sprint seansları daha etkilidir',
        'Günde 4-6 sprint seansı idealdir',
        'Kolay görevlerle başlayıp momentum kazanın',
        'Sprint sonrası başarılarınızı kaydedin'
      ]
    }
  },
  {
    id: 'microlearning',
    name: 'Micro-Learning',
    description: '15 dakika odaklanma, 5 dakika mola',
    workDuration: 15 * 60,
    breakDuration: 5 * 60,
    color: 'bg-pink-500',
    icon: '🧠',
    scientificBasis: 'Spaced repetition ve mikroöğrenme teorilerine dayanır. Hermann Ebbinghaus\'un unutma eğrisi araştırmaları, kısa aralıklarla tekrar edilen bilgilerin uzun vadeli hafızada %90 daha iyi korunduğunu gösterir.',
    benefits: [
      'Uzun vadeli hafıza oluşumu %90 artış',
      'Kolay sindirilebilir bilgi parçaları',
      'Düzenli tekrar ve pekiştirme',
      'Esnek öğrenme imkanı',
      'Bilgi transferini kolaylaştırır'
    ],
    effectiveSubjects: [
      'İngilizce (Kelime Dağarcığı)',
      'Matematik (Formül Ezber)',
      'Sosyal Bilgiler (Tarih Tarihleri)',
      'Fen Bilimleri (Terimler)',
      'Türkçe (Dil Bilgisi Kuralları)',
      'Din Kültürü (Kavramlar)'
    ],
    researchFindings: 'Journal of Educational Psychology\'de yayınlanan 2020 meta-analizi, micro-learning yöntemiyle öğrenen öğrencilerin bilgi kalıcılığının %85 daha yüksek olduğunu ve öğrenme hızının %60 arttığını göstermektedir.',
    howToApply: {
      preparation: [
        'Öğrenilecek konuyu küçük parçalara bölün',
        'Her seans için tek bir kavram/konu seçin',
        'Tekrar programı oluşturun (1 gün, 3 gün, 1 hafta, 1 ay)',
        'Mobil cihazınızda hatırlatıcılar kurun'
      ],
      duringWork: [
        '15 dakika boyunca sadece o konuya odaklanın',
        'Aktif öğrenme teknikleri kullanın (not alma, özetleme)',
        'Görsel ve işitsel materyalleri birleştirin',
        'Anlamadığınız yerleri işaretleyin'
      ],
      duringBreak: [
        '5 dakika zihinsel dinlenme yapın',
        'Öğrendiklerinizi zihinsel olarak tekrar edin',
        'Kısa yürüyüş yapın veya nefes alın',
        'Bir sonraki mikro-öğrenme seansını planlayın'
      ],
      tips: [
        'Günde 6-8 mikro-öğrenme seansı yapabilirsiniz',
        'Boş zamanları (otobüs, kuyruk) değerlendirin',
        'Spaced repetition uygulamaları kullanın',
        'İlerlemenizi takip edin ve ödüllendirin'
      ]
    }
  },
  {
    id: 'feynman',
    name: 'Feynman Tekniği',
    description: '30 dakika öğrenme, 15 dakika anlatma',
    workDuration: 30 * 60,
    breakDuration: 15 * 60,
    color: 'bg-indigo-500',
    icon: '🎓',
    scientificBasis: 'Nobel Fizik Ödülü sahibi Richard Feynman\'ın geliştirdiği bu teknik, "Öğretmek en iyi öğrenme yöntemidir" prensibine dayanır. Nörolojik araştırmalar, bilgiyi başkasına anlatmanın beyin bağlantılarını %70 güçlendirdiğini gösterir.',
    benefits: [
      'Derin anlama ve kavrama sağlar',
      'Bilgi kalıcılığını %80 artırır',
      'Eksik öğrenilen konuları tespit eder',
      'Özgüven ve sunum becerisini geliştirir',
      'Karmaşık konuları basitleştirir'
    ],
    effectiveSubjects: [
      'Matematik (Kavram Öğretimi)',
      'Fen Bilimleri (Deney Açıklama)',
      'Sosyal Bilgiler (Olay Anlatımı)',
      'Türkçe (Gramer Kuralları)',
      'İngilizce (Dil Bilgisi)',
      'Din Kültürü (Kavram Açıklama)'
    ],
    researchFindings: 'Carnegie Mellon Üniversitesi\'nin 2021 araştırmasında, Feynman tekniği kullanan öğrencilerin kavramsal anlama düzeyi %65 artmış ve sınav performansları %40 yükselmiştir.',
    howToApply: {
      preparation: [
        'Öğrenmek istediğiniz konuyu seçin',
        'Kağıt kalem hazırlayın (dijital araçlar kullanmayın)',
        'Sessiz bir ortam oluşturun',
        'Konuyu önce kendi başınıza öğrenin'
      ],
      duringWork: [
        'Konuyu 8 yaşındaki birine anlatır gibi yazın',
        'Basit kelimeler ve örnekler kullanın',
        'Anlamadığınız yerleri işaretleyin',
        'Analojiler ve metaforlar kullanın'
      ],
      duringBreak: [
        'İşaretlediğiniz yerleri tekrar öğrenin',
        'Kaynaklara geri dönün ve boşlukları doldurun',
        'Anladığınız kısımları pekiştirin',
        'Bir sonraki açıklama turunu planlayın'
      ],
      tips: [
        'Gerçekten birine anlatıyormuş gibi davranın',
        'Karmaşık terimleri basit kelimelerle değiştirin',
        'Görsel çizimler ve şemalar kullanın',
        'Anlattığınızı sesli okuyun'
      ]
    }
  },
  {
    id: 'active-recall',
    name: 'Aktif Hatırlama',
    description: '20 dakika çalışma, 10 dakika test',
    workDuration: 20 * 60,
    breakDuration: 10 * 60,
    color: 'bg-teal-500',
    icon: '🧩',
    scientificBasis: 'Cognitive Load Theory ve Testing Effect araştırmalarına dayanır. Beynin bilgiyi aktif olarak hatırlamaya çalışması, pasif okumaya göre %300 daha etkili öğrenme sağlar.',
    benefits: [
      'Uzun vadeli hafızayı güçlendirir',
      'Öğrenme hızını %200 artırır',
      'Bilgi transferini kolaylaştırır',
      'Sınav kaygısını azaltır',
      'Özgüveni artırır'
    ],
    effectiveSubjects: [
      'Matematik (Formül Hatırlama)',
      'İngilizce (Kelime Testi)',
      'Fen Bilimleri (Kavram Testi)',
      'Sosyal Bilgiler (Tarih Testi)',
      'Türkçe (Kural Hatırlama)',
      'Din Kültürü (Bilgi Testi)'
    ],
    researchFindings: 'Journal of Memory and Language\'de yayınlanan 2019 çalışması, aktif hatırlama yöntemiyle çalışan öğrencilerin bilgi kalıcılığının %90 daha yüksek olduğunu ve unutma hızının %60 azaldığını göstermektedir.',
    howToApply: {
      preparation: [
        'Öğrenilecek materyali bir kez okuyun',
        'Flashcard\'lar veya test soruları hazırlayın',
        'Zamanlayıcı kurun (20 dakika öğrenme)',
        'Dikkat dağıtıcıları kaldırın'
      ],
      duringWork: [
        '20 dakika aktif öğrenme yapın (not alma, özetleme)',
        'Kitabı kapatın ve hatırlamaya çalışın',
        'Kendinize sorular sorun ve cevaplayın',
        'Hatırladıklarınızı yazın'
      ],
      duringBreak: [
        '10 dakika kendinizi test edin',
        'Flashcard\'ları kullanın',
        'Hatırlayamadığınız yerleri işaretleyin',
        'Doğru cevapları kontrol edin'
      ],
      tips: [
        'Hatırlamaya çalışırken zorlanmak normaldir',
        'Yanlış hatırladıklarınızı not alın',
        'Düzenli aralıklarla tekrar yapın',
        'Farklı soru türleri kullanın'
      ]
    }
  },
  {
    id: 'interleaving',
    name: 'Karışık Çalışma',
    description: '40 dakika karma konu, 15 dakika mola',
    workDuration: 40 * 60,
    breakDuration: 15 * 60,
    color: 'bg-cyan-500',
    icon: '🔀',
    scientificBasis: 'Interleaving Effect araştırmalarına dayanır. Farklı konuları karışık sırayla çalışmak, beynin ayırt etme yeteneğini geliştirir ve öğrenmeyi %50 daha etkili hale getirir.',
    benefits: [
      'Problem çözme becerisini geliştirir',
      'Konular arası bağlantı kurar',
      'Yaratıcı düşünceyi artırır',
      'Sınav performansını %45 yükseltir',
      'Bilgi transferini kolaylaştırır'
    ],
    effectiveSubjects: [
      'Matematik (Farklı Konu Karması)',
      'Fen Bilimleri (Fizik-Kimya-Biyoloji)',
      'Sosyal Bilgiler (Tarih-Coğrafya)',
      'İngilizce (Gramer-Kelime-Okuma)',
      'Türkçe (Edebiyat-Dil Bilgisi)',
      'Tüm Dersler (Genel Tekrar)'
    ],
    researchFindings: 'Psychological Science dergisinde yayınlanan 2020 meta-analizi, karışık çalışma yöntemiyle öğrenen öğrencilerin problem çözme becerilerinin %55 arttığını ve bilgi transferinin %70 daha etkili olduğunu göstermektedir.',
    howToApply: {
      preparation: [
        '3-4 farklı konu veya ders seçin',
        'Her konu için 40 dakikalık materyaller hazırlayın',
        'Konuları rastgele sıralayın',
        'Geçiş planınızı önceden belirleyin'
      ],
      duringWork: [
        '40 dakika bir konuya odaklanın',
        'Zaman dolunca hemen diğer konuya geçin',
        'Önceki konuyu tamamen unutmaya çalışmayın',
        'Konular arası bağlantıları fark etmeye çalışın'
      ],
      duringBreak: [
        '15 dakika zihinsel geçiş yapın',
        'Önceki konudan çıkıp yenisine hazırlanın',
        'Kısa nefes egzersizi yapın',
        'Bir sonraki konunun materyallerini hazırlayın'
      ],
      tips: [
        'Benzer konuları karıştırmak daha etkilidir',
        'Başlangıçta zorlanmak normaldir',
        'Konular arası geçişi hızlı yapın',
        'Günde 3-4 karışık çalışma seansı idealdir'
      ]
    }
  }
];

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { startTechnique, pauseTechnique, resumeTechnique, stopTechnique } from '../store/slices/timeTechniqueSlice';
import { syncPlanWithFirebase } from '../store/slices/studySlice';
import { studyPlanService, authService } from '../services/firebase';

const TimeManagement: React.FC = () => {
  const [selectedTechnique, setSelectedTechnique] = useState<TimeTechnique>(TIME_TECHNIQUES[0]);
  const [timeLeft, setTimeLeft] = useState<number>(TIME_TECHNIQUES[0].workDuration);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isBreak, setIsBreak] = useState<boolean>(false);
  const [currentCycle, setCurrentCycle] = useState<number>(1);
  const [sessions, setSessions] = useState<TimeSession[]>([]);
  const [showStats, setShowStats] = useState<boolean>(false);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);
  
  // Hedefler için state'ler
  const [goals, setGoals] = useState<Goal[]>([]);
  const [showGoals, setShowGoals] = useState<boolean>(false);
  const [showAddGoal, setShowAddGoal] = useState<boolean>(false);
  const [newGoal, setNewGoal] = useState<Partial<Goal>>({
    title: '',
    description: '',
    targetSessions: 1,
    targetMinutes: 60,
    technique: 'pomodoro',
    deadline: '',
    priority: 'medium'
  });
  
  // Çalışma programı entegrasyonu için state'ler
  const [studyPlans, setStudyPlans] = useState<WeeklyPlan[]>([]);
  const [activeStudyPlan, setActiveStudyPlan] = useState<WeeklyPlan | null>(null);
  const [linkedSlot, setLinkedSlot] = useState<StudySlot | null>(null);
  const [showStudyPlanSelector, setShowStudyPlanSelector] = useState<boolean>(false);
  const [loadingPlans, setLoadingPlans] = useState<boolean>(false);
  
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerSectionRef = useRef<HTMLDivElement | null>(null);

  // Global timer state
  const dispatch = useDispatch();
  const timeTech = useSelector((state: RootState) => state.timeTechnique);
  const prevIsBreakRef = useRef<boolean | null>(null);

  // Çalışma planlarını yükle
  const loadStudyPlans = async () => {
    try {
      setLoadingPlans(true);
      const currentUser = authService.getCurrentUser();
      if (!currentUser) return;

      const plans = await studyPlanService.getUserPlans(currentUser.uid);
      setStudyPlans(plans);
      
      // Aktif planı bul
      const activePlan = plans.find(plan => plan.isActive);
      if (activePlan) {
        setActiveStudyPlan(activePlan);
      }
    } catch (error) {
      console.error('Çalışma planları yüklenirken hata:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  // Gelişmiş teknik önerme sistemi
  const getRecommendedTechnique = (slot: StudySlot): { technique: TimeTechnique; reason: string; confidence: number } => {
    const durationInMinutes = calculateSlotDuration(slot.time);
    
    // Konu bazlı öneriler ve gerekçeler
    const subjectRecommendations: { [key: string]: { techniques: string[]; reason: string } } = {
      'Matematik': { 
        techniques: ['pomodoro', 'active-recall', 'feynman'], 
        reason: 'LGS Matematik için problem çözme ve formül hatırlama teknikleri en etkilidir'
      },
      'Fen Bilimleri': { 
        techniques: ['pomodoro', 'feynman', 'active-recall'], 
        reason: 'LGS Fen Bilimleri için kavram öğrenme ve deney anlama teknikleri uygundur'
      },
      'Türkçe': { 
        techniques: ['flowtime', 'feynman', 'active-recall'], 
        reason: 'LGS Türkçe için metin analizi ve dil bilgisi kuralları için derin odaklanma gerekir'
      },
      'Sosyal Bilgiler': { 
        techniques: ['interleaving', 'active-recall', 'microlearning'], 
        reason: 'LGS Sosyal Bilgiler için tarih-coğrafya karışık çalışma ve tarih testi etkilidir'
      },
      'İngilizce': { 
        techniques: ['microlearning', 'sprint', 'active-recall'], 
        reason: 'LGS İngilizce için kelime dağarcığı ve gramer tekrarları en verimlidir'
      },
      'Din Kültürü ve Ahlak Bilgisi': { 
        techniques: ['microlearning', 'feynman', 'active-recall'], 
        reason: 'LGS Din Kültürü için kavram öğrenme ve bilgi testi teknikleri uygundur'
      }
    };
    
    const subjectData = subjectRecommendations[slot.subject] || {
      techniques: ['pomodoro'],
      reason: 'Genel çalışma için Pomodoro tekniği önerilir'
    };
    
    // Süre ve konu bazlı puanlama sistemi
    const techniqueScores = TIME_TECHNIQUES.map(technique => {
      const techniqueDuration = technique.workDuration / 60;
      const durationDiff = Math.abs(techniqueDuration - durationInMinutes);
      const durationScore = Math.max(0, 100 - (durationDiff * 3)); // Yakın süre = yüksek puan
      
      const subjectScore = subjectData.techniques.includes(technique.id) ? 60 : 0;
      const totalScore = durationScore + subjectScore;
      
      return {
        technique,
        score: totalScore,
        durationMatch: durationDiff <= 15
      };
    }).sort((a, b) => b.score - a.score);
    
    const bestMatch = techniqueScores[0];
    const confidence = Math.min(100, Math.max(50, bestMatch.score));
    
    return {
      technique: bestMatch.technique,
      reason: subjectData.reason,
      confidence
    };
  };
  
  // Basit teknik önerme (geriye uyumluluk için)
  const getSimpleRecommendedTechnique = (slotDuration: number): TimeTechnique => {
    const durationInMinutes = slotDuration;
    
    let bestTechnique = TIME_TECHNIQUES[0];
    let minDifference = Math.abs((bestTechnique.workDuration / 60) - durationInMinutes);
    
    TIME_TECHNIQUES.forEach(technique => {
      const techniqueDuration = technique.workDuration / 60;
      const difference = Math.abs(techniqueDuration - durationInMinutes);
      
      if (difference < minDifference) {
        minDifference = difference;
        bestTechnique = technique;
      }
    });
    
    return bestTechnique;
  };

  // Slot süresini hesapla (saat formatından dakikaya)
  const calculateSlotDuration = (timeSlot: string): number => {
    if (!timeSlot || typeof timeSlot !== 'string') {
      return 60; // Varsayılan 60 dakika
    }
    
    try {
      const [startTime, endTime] = timeSlot.split('-');
      if (!startTime || !endTime) {
        return 60; // Varsayılan 60 dakika
      }
      
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      
      if (isNaN(startHour) || isNaN(startMinute) || isNaN(endHour) || isNaN(endMinute)) {
        return 60; // Varsayılan 60 dakika
      }
      
      const startTotalMinutes = startHour * 60 + startMinute;
      const endTotalMinutes = endHour * 60 + endMinute;
      
      const duration = endTotalMinutes - startTotalMinutes;
      return duration > 0 ? duration : 60; // Negatif değer kontrolü
    } catch (error) {
      console.warn('Slot süresi hesaplanırken hata:', error);
      return 60; // Varsayılan 60 dakika
    }
  };

  // Çalışma slotunu tamamla
  const completeStudySlot = async (slot: StudySlot) => {
    if (!activeStudyPlan) return;
    
    try {
      // Slot'u tamamlanmış olarak işaretle
      const updatedSlots = activeStudyPlan.slots.map(s => 
        s.id === slot.id 
          ? { ...s, isCompleted: true, completedAt: new Date().toISOString() }
          : s
      );
      
      const updatedPlan = {
        ...activeStudyPlan,
        slots: updatedSlots,
        updatedAt: new Date().toISOString()
      };
      
      // Firebase'e kaydet
      await studyPlanService.updatePlan(updatedPlan.id, updatedPlan);
      
      // Local state'i güncelle
      setActiveStudyPlan(updatedPlan);
      
      // Redux store'u güncelle
      dispatch(syncPlanWithFirebase(updatedPlan));
      
      console.log('Çalışma slotu tamamlandı:', slot.subject, slot.topic);
    } catch (error) {
      console.error('Slot tamamlanırken hata:', error);
    }
  };

  // Replace local interval with global ticker (skip local interval when global timer is started)
  useEffect(() => {
    if (timeTech.started) {
      // Keep local UI in sync with global state
      setTimeLeft(timeTech.timeLeft);
      setIsActive(timeTech.isActive);
      setIsBreak(timeTech.isBreak);
      setCurrentCycle(timeTech.currentCycle);
      return; // prevent setting up local interval
    }

    if (isActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleTimerComplete();
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, timeLeft, timeTech.started]);

  // Detect phase transitions in global timer to push sessions and play sound
  useEffect(() => {
    if (!timeTech.started) return;
    const prev = prevIsBreakRef.current;
    if (prev !== null && prev !== timeTech.isBreak) {
      // Phase changed
      if (soundEnabled && audioRef.current) {
        audioRef.current.play().catch(e => console.log('Ses çalınamadı:', e));
      }
      if (timeTech.isBreak) {
        // Work finished -> record session
        const newSession: TimeSession = {
          id: Date.now().toString(),
          technique: timeTech.technique?.name || selectedTechnique.name,
          duration: timeTech.technique?.workDuration || selectedTechnique.workDuration,
          completed: true,
          date: new Date().toISOString()
        };
        setSessions(prevSessions => [...prevSessions, newSession]);
      }
    }
    prevIsBreakRef.current = timeTech.isBreak;
  }, [timeTech.isBreak, timeTech.started, soundEnabled, selectedTechnique.name, selectedTechnique.workDuration]);

  const handleTimerComplete = () => {
    setIsActive(false);
    
    if (soundEnabled && audioRef.current) {
      audioRef.current.play().catch(e => console.log('Ses çalınamadı:', e));
    }

    if (!isBreak) {
      // Çalışma süresi bitti, mola zamanı
      const newSession: TimeSession = {
        id: Date.now().toString(),
        technique: selectedTechnique.name,
        duration: selectedTechnique.workDuration,
        completed: true,
        date: new Date().toISOString()
      };
      setSessions(prev => [...prev, newSession]);

      setIsBreak(true);
      
      // Uzun mola kontrolü (Pomodoro için)
      if (selectedTechnique.id === 'pomodoro' && currentCycle % 4 === 0) {
        setTimeLeft(selectedTechnique.longBreakDuration || selectedTechnique.breakDuration);
      } else {
        setTimeLeft(selectedTechnique.breakDuration);
      }
    } else {
      // Mola bitti, yeni çalışma süresi
      setIsBreak(false);
      setTimeLeft(selectedTechnique.workDuration);
      setCurrentCycle(prev => prev + 1);
    }
  };

  const toggleTimer = () => {
    // If global timer hasn't started yet, start with selected technique
    if (!timeTech.started) {
      dispatch(startTechnique({
        techniqueId: selectedTechnique.id,
        name: selectedTechnique.name,
        workDuration: selectedTechnique.workDuration,
        breakDuration: selectedTechnique.breakDuration,
        longBreakDuration: selectedTechnique.longBreakDuration,
        cycles: selectedTechnique.cycles,
        color: selectedTechnique.color,
        icon: selectedTechnique.icon,
      }));
      return;
    }
    // Otherwise toggle pause/resume on global timer
    if (timeTech.isActive) {
      dispatch(pauseTechnique());
    } else {
      dispatch(resumeTechnique());
    }
  };

  const resetTimer = () => {
    // Stop global timer if started
    if (timeTech.started) {
      dispatch(stopTechnique());
    }
    setIsActive(false);
    setIsBreak(false);
    setTimeLeft(selectedTechnique.workDuration);
    setCurrentCycle(1);
  };

  const changeTechnique = (technique: TimeTechnique) => {
    setSelectedTechnique(technique);
    setTimeLeft(technique.workDuration);
    setIsActive(false);
    setIsBreak(false);
    setCurrentCycle(1);

    // Teknik (süre) seçildikten sonra sayaç bölümüne kaydır
    requestAnimationFrame(() => {
      if (timerSectionRef.current) {
        timerSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  useEffect(() => {
    if (!timeTech.started) return;
    // Mirror global timer values into local UI state on every change
    setTimeLeft(timeTech.timeLeft);
    setIsActive(timeTech.isActive);
    setIsBreak(timeTech.isBreak);
    setCurrentCycle(timeTech.currentCycle);
  }, [timeTech.timeLeft, timeTech.isActive, timeTech.isBreak, timeTech.currentCycle, timeTech.started]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgress = (): number => {
    let totalTime: number;
    if (timeTech.started && timeTech.technique) {
      const isPomodoroLongBreak = timeTech.technique.techniqueId === 'pomodoro' && !!timeTech.technique.longBreakDuration && (currentCycle % 4 === 0) && isBreak;
      totalTime = isBreak ? (isPomodoroLongBreak ? (timeTech.technique.longBreakDuration as number) : timeTech.technique.breakDuration) : timeTech.technique.workDuration;
    } else {
      totalTime = isBreak ? selectedTechnique.breakDuration : selectedTechnique.workDuration;
    }
    return ((totalTime - timeLeft) / totalTime) * 100;
  };

  const getTodayStats = () => {
    const today = new Date().toDateString();
    const todaySessions = sessions.filter(session => 
      new Date(session.date).toDateString() === today
    );
    
    const totalMinutes = todaySessions.reduce((acc, session) => 
      acc + (session.duration / 60), 0
    );
    
    return {
      sessions: todaySessions.length,
      minutes: Math.round(totalMinutes),
      techniques: [...new Set(todaySessions.map(s => s.technique))].length
    };
  };

  const stats = getTodayStats();

  // Hedef yönetimi fonksiyonları
  const addGoal = () => {
    if (!newGoal.title || !newGoal.deadline) return;
    
    const goal: Goal = {
      id: Date.now().toString(),
      title: newGoal.title!,
      description: newGoal.description || '',
      targetSessions: newGoal.targetSessions || 1,
      completedSessions: 0,
      targetMinutes: newGoal.targetMinutes || 60,
      completedMinutes: 0,
      technique: newGoal.technique || 'pomodoro',
      deadline: newGoal.deadline!,
      priority: newGoal.priority || 'medium',
      completed: false,
      createdAt: new Date().toISOString()
    };
    
    setGoals(prev => [...prev, goal]);
    setNewGoal({
      title: '',
      description: '',
      targetSessions: 1,
      targetMinutes: 60,
      technique: 'pomodoro',
      deadline: '',
      priority: 'medium'
    });
    setShowAddGoal(false);
  };

  const updateGoalProgress = (goalId: string, sessions: number, minutes: number) => {
    setGoals(prev => prev.map(goal => {
      if (goal.id === goalId) {
        const updatedGoal = {
          ...goal,
          completedSessions: goal.completedSessions + sessions,
          completedMinutes: goal.completedMinutes + minutes
        };
        updatedGoal.completed = updatedGoal.completedSessions >= updatedGoal.targetSessions && 
                                updatedGoal.completedMinutes >= updatedGoal.targetMinutes;
        return updatedGoal;
      }
      return goal;
    }));
  };

  const deleteGoal = (goalId: string) => {
    setGoals(prev => prev.filter(goal => goal.id !== goalId));
  };

  const toggleGoalComplete = (goalId: string) => {
    setGoals(prev => prev.map(goal => 
      goal.id === goalId ? { ...goal, completed: !goal.completed } : goal
    ));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getGoalProgress = (goal: Goal) => {
    const sessionProgress = (goal.completedSessions / goal.targetSessions) * 100;
    const minuteProgress = (goal.completedMinutes / goal.targetMinutes) * 100;
    return Math.min(sessionProgress, minuteProgress);
  };

  // Hedef güncelleme - timer tamamlandığında çağrılacak
  useEffect(() => {
    if (sessions.length > 0) {
      const lastSession = sessions[sessions.length - 1];
      const matchingGoals = goals.filter(goal => 
        goal.technique === selectedTechnique.id && !goal.completed
      );
      
      matchingGoals.forEach(goal => {
        updateGoalProgress(goal.id, 1, lastSession.duration / 60);
      });
    }
  }, [sessions]);

  // Çalışma planlarını yükle
  useEffect(() => {
    loadStudyPlans();
  }, []);

  // Aktif çalışma planından otomatik hedef oluştur
  useEffect(() => {
    if (activeStudyPlan && activeStudyPlan.slots.length > 0) {
      const incompleteSlots = activeStudyPlan.slots.filter(slot => !slot.isCompleted);
      
      // Her konu için ayrı hedef oluştur
      const subjectGoals: Goal[] = [];
      const subjectMap = new Map<string, StudySlot[]>();
      
      incompleteSlots.forEach(slot => {
        const key = `${slot.subject}-${slot.topic}`;
        if (!subjectMap.has(key)) {
          subjectMap.set(key, []);
        }
        subjectMap.get(key)!.push(slot);
      });
      
      subjectMap.forEach((slots, key) => {
        const [subject, topic] = key.split('-');
        const totalDuration = slots.reduce((sum, slot) => {
          return sum + calculateSlotDuration(slot.time);
        }, 0);
        
        const recommendedTechnique = getRecommendedTechnique(totalDuration / slots.length);
        
        const goal: Goal = {
          id: `study-${Date.now()}-${Math.random()}`,
          title: `${subject} - ${topic}`,
          description: `${activeStudyPlan.title} planından otomatik oluşturuldu`,
          targetSessions: slots.length,
          completedSessions: 0,
          targetMinutes: totalDuration,
          completedMinutes: 0,
          technique: recommendedTechnique.id,
          deadline: activeStudyPlan.endDate,
          priority: 'high',
          completed: false,
          createdAt: new Date().toISOString()
        };
        
        subjectGoals.push(goal);
      });
      
      // Mevcut otomatik hedefleri temizle ve yenilerini ekle
      setGoals(prevGoals => {
        const manualGoals = prevGoals.filter(goal => !goal.description.includes('otomatik oluşturuldu'));
        return [...manualGoals, ...subjectGoals];
      });
    }
  }, [activeStudyPlan]);

  // Timer tamamlandığında çalışma slotunu otomatik tamamla
  useEffect(() => {
    if (!timeTech.started) return;
    const prev = prevIsBreakRef.current;
    if (prev !== null && prev !== timeTech.isBreak && timeTech.isBreak && linkedSlot) {
      // Çalışma süresi bitti, bağlı slotu tamamla
      completeStudySlot(linkedSlot);
      setLinkedSlot(null);
    }
  }, [timeTech.isBreak, linkedSlot]);

  // Local timer tamamlandığında da slot'u tamamla
  useEffect(() => {
    if (!isActive && !isBreak && linkedSlot && sessions.length > 0) {
      const lastSession = sessions[sessions.length - 1];
      if (lastSession && new Date(lastSession.date).getTime() > Date.now() - 5000) {
        completeStudySlot(linkedSlot);
        setLinkedSlot(null);
      }
    }
  }, [isActive, isBreak, linkedSlot, sessions]);

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-white mb-2">
            Süre Teknikleri
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
            Bilimsel zaman yönetimi teknikleriyle verimliliğinizi artırın
          </p>
        </div>

        {/* Technique Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {TIME_TECHNIQUES.map((technique) => (
            <div
              key={technique.id}
              onClick={() => changeTechnique(technique)}
              className={`p-3 sm:p-4 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                selectedTechnique.id === technique.id
                  ? `${technique.color} text-white shadow-lg`
                  : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-white shadow-md hover:shadow-lg'
              }`}
            >
              <div className="flex items-center mb-2">
                <span className="text-xl sm:text-2xl mr-2 sm:mr-3">{technique.icon}</span>
                <h3 className="font-semibold text-base sm:text-lg">{technique.name}</h3>
              </div>
              <p className={`text-xs sm:text-sm ${
                selectedTechnique.id === technique.id 
                  ? 'text-white/90' 
                  : 'text-gray-600 dark:text-gray-300'
              }`}>
                {technique.description}
              </p>
            </div>
          ))}
        </div>

        {/* Main Timer */}
        <div ref={timerSectionRef} className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
          <div className="text-center">
            {/* Timer Display */}
            <div className="relative mb-6 sm:mb-8">
              <div className="w-48 h-48 sm:w-56 sm:h-56 lg:w-64 lg:h-64 mx-auto relative">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    className="text-gray-200 dark:text-gray-700"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 45}`}
                    strokeDashoffset={`${2 * Math.PI * 45 * (1 - getProgress() / 100)}`}
                    className={`${selectedTechnique.color.replace('bg-', 'text-')} transition-all duration-1000`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-gray-800 dark:text-white mb-2">
                      {formatTime(timeLeft)}
                    </div>
                    <div className={`text-xs sm:text-sm font-medium ${
                      isBreak ? 'text-green-600' : selectedTechnique.color.replace('bg-', 'text-')
                    }`}>
                      {isBreak ? 'Mola Zamanı' : 'Çalışma Zamanı'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Technique Info */}
            <div className="mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white mb-2">
                {selectedTechnique.name}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Döngü: {currentCycle} {selectedTechnique.cycles && `/ ${selectedTechnique.cycles}`}
              </p>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
              <button
                onClick={toggleTimer}
                className={`${selectedTechnique.color} text-white px-6 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold flex items-center space-x-2 hover:opacity-90 transition-opacity w-full sm:w-auto`}
              >
                {isActive ? <Pause size={18} /> : <Play size={18} />}
                <span>{isActive ? 'Duraklat' : 'Başlat'}</span>
              </button>
              <button
                onClick={resetTimer}
                className="bg-gray-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold flex items-center space-x-2 hover:bg-gray-600 transition-colors w-full sm:w-auto"
              >
                <RotateCcw size={18} />
                <span>Sıfırla</span>
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setShowStats(true)}
                className="bg-blue-500 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-600 transition-colors w-full sm:w-auto text-sm sm:text-base"
              >
                <BarChart3 size={16} />
                <span>İstatistikler</span>
              </button>
              <button
                onClick={() => setShowInfo(true)}
                className="bg-green-500 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-600 transition-colors w-full sm:w-auto text-sm sm:text-base"
              >
                <Info size={16} />
                <span>Teknik Bilgisi</span>
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="bg-purple-500 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-purple-600 transition-colors w-full sm:w-auto text-sm sm:text-base"
              >
                <Settings size={16} />
                <span>Ayarlar</span>
              </button>
            </div>
          </div>
        </div>

        {/* Today's Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">{stats.sessions}</div>
            <div className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Tamamlanan Oturum</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">{stats.minutes}</div>
            <div className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Dakika Çalışma</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-2">{stats.techniques}</div>
            <div className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Farklı Teknik</div>
          </div>
        </div>



        {/* Stats Modal */}
        {showStats && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                Günlük İstatistikler
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Toplam Oturum:</span>
                  <span className="font-semibold text-gray-800 dark:text-white">{stats.sessions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Toplam Süre:</span>
                  <span className="font-semibold text-gray-800 dark:text-white">{stats.minutes} dakika</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Kullanılan Teknik:</span>
                  <span className="font-semibold text-gray-800 dark:text-white">{stats.techniques}</span>
                </div>
              </div>
              <button
                onClick={() => setShowStats(false)}
                className="w-full mt-6 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        )}

        {/* Teknik Bilgisi Modal */}
        {showInfo && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-2xl p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide shadow-2xl border border-white/20 dark:border-gray-700/30">
              <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200/50 dark:border-gray-700/50">
                <div className="flex items-center space-x-3">
                  <div className="text-3xl">{selectedTechnique.icon}</div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                    {selectedTechnique.name}
                  </h3>
                </div>
                <button
                  onClick={() => setShowInfo(false)}
                  className="group relative p-2 rounded-full bg-gray-100/50 dark:bg-gray-800/50 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all duration-200 hover:scale-110"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-red-500 transition-colors" />
                  <div className="absolute inset-0 rounded-full bg-red-500/20 scale-0 group-hover:scale-100 transition-transform duration-200"></div>
                </button>
              </div>
              
              {/* Nasıl Uygulanır - En Üstte */}
              {selectedTechnique.howToApply && (
                <div className="mb-8 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-indigo-900/20 p-8 rounded-2xl border border-purple-200/50 dark:border-purple-700/30 shadow-lg backdrop-blur-sm">
                  <div className="flex items-center mb-8">
                    <div className="p-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl mr-4 shadow-lg">
                      <span className="text-2xl">🎯</span>
                    </div>
                    <h4 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                      Nasıl Uygulanır?
                    </h4>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Hazırlık */}
                    <div className="group bg-white/60 dark:bg-gray-800/60 p-6 rounded-xl border border-purple-200/30 dark:border-purple-700/30 hover:shadow-lg transition-all duration-300 hover:scale-105">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-gradient-to-r from-green-400 to-emerald-500 rounded-lg mr-3 shadow-md">
                          <span className="text-lg">🔧</span>
                        </div>
                        <h5 className="font-semibold text-gray-800 dark:text-gray-200 text-base">
                          Hazırlık
                        </h5>
                      </div>
                      <ul className="space-y-3">
                        {selectedTechnique.howToApply.preparation.map((item, index) => (
                          <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                            <div className="w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Çalışma Sırasında */}
                    <div className="group bg-white/60 dark:bg-gray-800/60 p-6 rounded-xl border border-blue-200/30 dark:border-blue-700/30 hover:shadow-lg transition-all duration-300 hover:scale-105">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-lg mr-3 shadow-md">
                          <span className="text-lg">📖</span>
                        </div>
                        <h5 className="font-semibold text-gray-800 dark:text-gray-200 text-base">
                          Çalışma Sırasında
                        </h5>
                      </div>
                      <ul className="space-y-3">
                        {selectedTechnique.howToApply.duringWork.map((item, index) => (
                          <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                            <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Mola Sırasında */}
                    <div className="group bg-white/60 dark:bg-gray-800/60 p-6 rounded-xl border border-orange-200/30 dark:border-orange-700/30 hover:shadow-lg transition-all duration-300 hover:scale-105">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-gradient-to-r from-orange-400 to-amber-500 rounded-lg mr-3 shadow-md">
                          <span className="text-lg">☕</span>
                        </div>
                        <h5 className="font-semibold text-gray-800 dark:text-gray-200 text-base">
                          Mola Sırasında
                        </h5>
                      </div>
                      <ul className="space-y-3">
                        {selectedTechnique.howToApply.duringBreak.map((item, index) => (
                          <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                            <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* İpuçları */}
                    <div className="group bg-white/60 dark:bg-gray-800/60 p-6 rounded-xl border border-yellow-200/30 dark:border-yellow-700/30 hover:shadow-lg transition-all duration-300 hover:scale-105">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-lg mr-3 shadow-md">
                          <span className="text-lg">💡</span>
                        </div>
                        <h5 className="font-semibold text-gray-800 dark:text-gray-200 text-base">
                          İpuçları
                        </h5>
                      </div>
                      <ul className="space-y-3">
                        {selectedTechnique.howToApply.tips.map((item, index) => (
                          <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                            <div className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                {/* Sol Kolon */}
                <div className="space-y-6">
                  {/* Temel Bilgiler */}
                  <div className="group bg-white/70 dark:bg-gray-800/70 p-6 rounded-xl border border-gray-200/40 dark:border-gray-700/40 hover:shadow-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm">
                    <div className="flex items-center mb-4">
                      <div className="p-3 bg-gradient-to-r from-gray-400 to-slate-500 rounded-lg mr-3 shadow-lg">
                        <span className="text-xl">⏱️</span>
                      </div>
                      <h4 className="font-bold text-gray-800 dark:text-gray-100 text-lg">
                        Süre Detayları
                      </h4>
                    </div>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Çalışma süresi:</span>
                        <span className="font-bold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 px-3 py-1 rounded-full text-sm shadow-sm">{Math.floor(selectedTechnique.workDuration / 60)} dakika</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Mola süresi:</span>
                        <span className="font-bold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 px-3 py-1 rounded-full text-sm shadow-sm">{Math.floor(selectedTechnique.breakDuration / 60)} dakika</span>
                      </div>
                      {selectedTechnique.longBreakDuration && (
                        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Uzun mola:</span>
                          <span className="font-bold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 px-3 py-1 rounded-full text-sm shadow-sm">{Math.floor(selectedTechnique.longBreakDuration / 60)} dakika</span>
                        </div>
                      )}
                      {selectedTechnique.cycles && (
                        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Döngü sayısı:</span>
                          <span className="font-bold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 px-3 py-1 rounded-full text-sm shadow-sm">{selectedTechnique.cycles}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bilimsel Temel */}
                  {selectedTechnique.scientificBasis && (
                    <div className="group bg-white/70 dark:bg-gray-800/70 p-6 rounded-xl border border-blue-200/40 dark:border-blue-700/40 hover:shadow-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm">
                      <div className="flex items-center mb-4">
                        <div className="p-3 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-lg mr-3 shadow-lg">
                          <span className="text-xl">🧬</span>
                        </div>
                        <h4 className="font-bold text-gray-800 dark:text-gray-100 text-lg">
                          Bilimsel Temel
                        </h4>
                      </div>
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-4 rounded-lg border-l-4 border-blue-400">
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                          {selectedTechnique.scientificBasis}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Araştırma Bulguları */}
                  {selectedTechnique.researchFindings && (
                    <div className="group bg-white/70 dark:bg-gray-800/70 p-6 rounded-xl border border-green-200/40 dark:border-green-700/40 hover:shadow-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm">
                      <div className="flex items-center mb-4">
                        <div className="p-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-lg mr-3 shadow-lg">
                          <span className="text-xl">📊</span>
                        </div>
                        <h4 className="font-bold text-gray-800 dark:text-gray-100 text-lg">
                          Araştırma Bulguları
                        </h4>
                      </div>
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-4 rounded-lg border-l-4 border-green-400">
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                          {selectedTechnique.researchFindings}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sağ Kolon */}
                <div className="space-y-6">
                  {/* Faydalar */}
                  {selectedTechnique.benefits && (
                    <div className="group bg-white/70 dark:bg-gray-800/70 p-6 rounded-xl border border-purple-200/40 dark:border-purple-700/40 hover:shadow-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm">
                      <div className="flex items-center mb-4">
                        <div className="p-3 bg-gradient-to-r from-purple-400 to-violet-500 rounded-lg mr-3 shadow-lg">
                          <span className="text-xl">✨</span>
                        </div>
                        <h4 className="font-bold text-gray-800 dark:text-gray-100 text-lg">
                          Bilimsel Faydalar
                        </h4>
                      </div>
                      <div className="space-y-3">
                        {selectedTechnique.benefits.map((benefit, index) => (
                          <div key={index} className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 p-4 rounded-lg border-l-4 border-purple-400 hover:shadow-md transition-shadow">
                            <div className="flex items-start">
                              <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-violet-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium leading-relaxed">{benefit}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Etkili Olduğu Dersler */}
                  {selectedTechnique.effectiveSubjects && (
                    <div className="group bg-white/70 dark:bg-gray-800/70 p-6 rounded-xl border border-orange-200/40 dark:border-orange-700/40 hover:shadow-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm">
                      <div className="flex items-center mb-4">
                        <div className="p-3 bg-gradient-to-r from-orange-400 to-amber-500 rounded-lg mr-3 shadow-lg">
                          <span className="text-xl">📚</span>
                        </div>
                        <h4 className="font-bold text-gray-800 dark:text-gray-100 text-lg">
                          Etkili Olduğu Ders Türleri
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        {selectedTechnique.effectiveSubjects.map((subject, index) => (
                          <div key={index} className="group/item bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 p-4 rounded-lg border border-orange-200/50 dark:border-orange-700/50 hover:shadow-md hover:scale-105 transition-all duration-200">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-gradient-to-r from-orange-400 to-amber-500 rounded-full mr-3 flex-shrink-0 group-hover/item:scale-110 transition-transform"></div>
                              <span className="text-sm text-gray-700 dark:text-gray-300 font-semibold group-hover/item:text-gray-900 dark:group-hover/item:text-gray-100 transition-colors">
                                {subject}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Alt Bilgi */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Bu bilgiler akademik araştırmalara dayanmaktadır. Kişisel verimlilik farklılık gösterebilir.
                </p>
              </div>
            </div>
          </div>
        )}



        {/* Add Goal Modal */}
        {showAddGoal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
                <Target className="mr-2 text-indigo-600" size={24} />
                Yeni Hedef Ekle
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Hedef Başlığı *
                  </label>
                  <input
                    type="text"
                    value={newGoal.title || ''}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Örn: Matematik sınavına hazırlanmak"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Açıklama
                  </label>
                  <textarea
                    value={newGoal.description || ''}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Hedef hakkında detaylar..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hedef Oturum
                    </label>
                    <input
                      type="number"
                      value={newGoal.targetSessions || 1}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, targetSessions: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hedef Dakika
                    </label>
                    <input
                      type="number"
                      value={newGoal.targetMinutes || 60}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, targetMinutes: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Teknik
                  </label>
                  <select
                    value={newGoal.technique || 'pomodoro'}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, technique: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {TIME_TECHNIQUES.map(technique => (
                      <option key={technique.id} value={technique.id}>
                        {technique.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Son Tarih *
                    </label>
                    <input
                      type="date"
                      value={newGoal.deadline || ''}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, deadline: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Öncelik
                    </label>
                    <select
                      value={newGoal.priority || 'medium'}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="low">Düşük</option>
                      <option value="medium">Orta</option>
                      <option value="high">Yüksek</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowAddGoal(false)}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={addGoal}
                  disabled={!newGoal.title || !newGoal.deadline}
                  className="flex-1 bg-indigo-500 text-white py-2 rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hedef Ekle
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                Ayarlar
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Volume2 size={20} className="text-gray-600 dark:text-gray-300" />
                    <span className="text-gray-600 dark:text-gray-300">Ses Bildirimleri</span>
                  </div>
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      soundEnabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      soundEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="w-full mt-6 bg-purple-500 text-white py-2 rounded-lg hover:bg-purple-600 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        )}

        {/* Audio Element */}
        <audio
          ref={audioRef}
          preload="auto"
        >
          <source src="/notification.mp3" type="audio/mpeg" />
        </audio>
      </div>
    </div>
  );
};

export default TimeManagement;